import React, { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardPagination from "../components/DashboardPagination";
import { DASHBOARD_LIST_PAGE_SIZE } from "../constants/dashboardListPagination";
import { IRentedPropertyDetail } from "../types/ownerDashboard.types";
import { normalizeRentedPropertiesDetails } from "../utils/normalizeRentedPropertiesDetails";
import { paginateItemsLocally } from "../utils/paginateItemsLocally";
import RentedPropertyCard from "./RentedPropertyCard";

interface RentedPropertiesProps {
  properties?: IRentedPropertyDetail[] | null;
  smDown: boolean;
  lgDown?: boolean;
}

function getCardPadding(smDown: boolean, lgDown: boolean) {
  if (smDown) return "0.875rem";
  if (lgDown) return "1rem";
  return "1.125rem";
}

export default function RentedProperties({
  properties,
  smDown,
  lgDown = false,
}: RentedPropertiesProps) {
  const [page, setPage] = useState(1);
  const rentedProperties = normalizeRentedPropertiesDetails(properties);
  const { items: visibleProperties, pagination } = paginateItemsLocally(
    rentedProperties,
    page,
    DASHBOARD_LIST_PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [rentedProperties.length]);

  useEffect(() => {
    if (page > pagination.last_page) {
      setPage(pagination.last_page);
    }
  }, [page, pagination.last_page]);

  if (rentedProperties.length === 0) {
    return null;
  }

  const padding = getCardPadding(smDown, lgDown);
  const vacatingCount = rentedProperties.filter(
    (property) => property.is_vacating === true
  ).length;

  return (
    <DashboardCard padding={padding}>
      <div className="mb-4 flex items-start gap-3">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-[#6ea8ff]" aria-hidden />
        <div className="min-w-0">
          <h2 className="m-0 text-[clamp(1rem,1.4vw,1.25rem)] font-semibold text-[#f0f6fc]">
            Imóveis alugados
          </h2>
          <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
            Acompanhe o status de locação e desocupações em andamento
            {vacatingCount > 0
              ? ` • ${vacatingCount} em desocupação`
              : ""}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3" data-testid="rented-properties-list">
        {visibleProperties.map((property) => (
          <RentedPropertyCard key={property.property_id} property={property} />
        ))}
      </div>

      <DashboardPagination
        currentPage={pagination.current_page}
        totalPages={pagination.last_page}
        onPageChange={setPage}
      />
    </DashboardCard>
  );
}
